import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { campoBase, campoEstado, RotuloCampo, MensagemCampo } from './InputField';

interface ButtonProps {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: React.FC<ButtonProps> = ({
  label,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  fullWidth = false,
  type = 'button',
}) => {
  const variantStyles = {
    primary: 'bg-[#D92B2B] text-white hover:bg-[#B71F1F] shadow-sm',
    secondary: 'bg-white text-slate-900 border border-slate-300 hover:bg-slate-50',
    danger: 'bg-red-700 text-white hover:bg-red-800 shadow-sm',
    success: 'bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm',
  };

  const sizeStyles = {
    sm: 'h-9 px-4 text-sm rounded-lg',
    md: 'h-12 px-6 text-[15px] rounded-xl',
    lg: 'h-14 px-8 text-base rounded-xl',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 font-bold transition-colors
        disabled:cursor-not-allowed disabled:opacity-50
        ${variantStyles[variant]} ${sizeStyles[size]} ${fullWidth ? 'w-full' : ''}`}
      aria-busy={loading}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : icon}
      {label}
    </button>
  );
};

interface SelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  error?: string;
  help?: string;
  disabled?: boolean;
  hideBottomMargin?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  error,
  help,
  disabled = false,
  hideBottomMargin = false,
}) => {
  const valido = !!value && !error;
  return (
    <div className={hideBottomMargin ? '' : 'mb-6'}>
      <RotuloCampo htmlFor={name} label={label} required={required} valido={valido} />
      <div className="relative">
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : help ? `${name}-help` : undefined}
          className={`${campoBase} h-12 pl-4 pr-10 appearance-none cursor-pointer ${campoEstado(error, valido)}
            ${disabled ? '!bg-slate-100 text-slate-500 cursor-not-allowed !border-slate-200' : ''}`}
        >
          <option value="">Selecione uma opção...</option>
          {options.map((option, idx) => (
            <option key={`${option.value}-${idx}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" aria-hidden="true" />
      </div>
      <MensagemCampo name={name} error={error} help={help} />
    </div>
  );
};

interface TextAreaProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  error?: string;
  help?: string;
  disabled?: boolean;
  maxLength?: number;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  rows = 4,
  error,
  help,
  disabled = false,
  maxLength,
}) => {
  const valido = value.length > 0 && !error;
  return (
    <div className="mb-6">
      <RotuloCampo htmlFor={name} label={label} required={required} valido={valido} />
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        required={required}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : help ? `${name}-help` : undefined}
        className={`${campoBase} px-4 py-3 leading-relaxed resize-y ${campoEstado(error, valido)}
          ${disabled ? '!bg-slate-100 text-slate-500 cursor-not-allowed !border-slate-200' : ''}`}
      />
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1"><MensagemCampo name={name} error={error} help={help} /></div>
        {maxLength && (
          <span className="mt-1.5 text-xs text-slate-500 tabular-nums">{value.length} / {maxLength}</span>
        )}
      </div>
    </div>
  );
};

export default { Button, Select, TextArea };
